import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

// Inline-Registrierung ("Konto sichern") — verwandelt den Gast-Account in ein echtes Konto.
export default function GuestClaimModal({ open, onOpenChange, title = "Konto sichern", intro = "Sichere deine Eingaben in einem kostenlosen Konto — deine Kunden, Projekte und Rechnungen wandern automatisch mit. Nach dem Sichern gilt der Free-Plan (jederzeit upgradebar); die Demo-Beispieldaten werden entfernt. Nur E-Mail und Passwort nötig." }) {
  const { claimGuest } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Bitte E-Mail und Passwort eingeben.");
      return;
    }
    if (password.length < 8) {
      toast.error("Das Passwort muss mindestens 8 Zeichen haben.");
      return;
    }
    if (!acceptedTerms) {
      toast.error("Bitte akzeptiere die AGB, um dein Konto zu sichern.");
      return;
    }
    setSaving(true);
    try {
      await claimGuest({ email: email.trim(), password, acceptedTerms: true });
      toast.success("Konto gesichert! Deine Eingaben bleiben erhalten.");
      onOpenChange(false);
      navigate("/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Konto konnte nicht gesichert werden. Bitte erneut versuchen.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="guest-claim-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          {intro}
        </p>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="claim-email">E-Mail</Label>
            <Input
              id="claim-email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="du@beispiel.de" data-testid="guest-claim-email"
              autoComplete="email" required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="claim-password">Passwort (mind. 8 Zeichen)</Label>
            <Input
              id="claim-password" type="password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" data-testid="guest-claim-password"
              autoComplete="new-password" required
            />
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox" checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-0.5" data-testid="guest-claim-accept-terms"
            />
            <span>
              Ich akzeptiere die{" "}
              <a href="/terms" target="_blank" rel="noreferrer" className="text-emerald-700 underline">AGB</a>.
            </span>
          </label>
          <Button
            type="submit" disabled={saving}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="guest-claim-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Konto sichern
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
